import type { Chunk, Usage } from './types.js';

interface SSEEvent {
  choices?: Array<{
    delta?: {
      content?: string;
      reasoning_content?: string;
      tool_calls?: unknown[];
    };
    finish_reason?: string;
  }>;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
}

/**
 * Parse SSE-formatted data into a chunk and optional usage.
 * Extracts content, reasoning_content, and usage from OpenAI-compatible SSE events.
 */
export function parseSSEChunks(data: string): { chunk: Chunk; usage: Usage | null } {
  let content = '';
  let reasoningContent = '';
  let toolCalls: unknown[] | undefined = undefined;
  let usage: Usage | null = null;

  for (const line of data.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data: ')) continue;

    const payload = trimmed.slice(6);
    if (payload === '[DONE]') continue;

    let event: SSEEvent;
    try {
      event = JSON.parse(payload);
    } catch {
      continue;
    }

    if (event.choices && event.choices.length > 0) {
      const delta = event.choices[0].delta;
      if (delta?.content) content += delta.content;
      if (delta?.reasoning_content) reasoningContent += delta.reasoning_content;
      if (delta?.tool_calls && Array.isArray(delta.tool_calls) && delta.tool_calls.length > 0) {
        // Single-call passthrough: the upstream emits one SSE event with the
        // complete tool_calls array, so no delta aggregation is needed at this
        // layer. Last event with tool_calls wins; streaming-aggregated
        // arguments deltas are out of scope for this surface.
        toolCalls = delta.tool_calls;
      }
    }

    if (event.usage && event.usage.total_tokens && event.usage.total_tokens > 0) {
      usage = {
        promptTokens: event.usage.prompt_tokens ?? 0,
        cachedTokens: 0,
        completionTokens: event.usage.completion_tokens ?? 0,
        reasoningTokens: 0,
        totalTokens: event.usage.total_tokens,
        model: '',
        verified: false,
      };
    }
  }

  const chunk: Chunk = { content, reasoningContent };
  if (toolCalls !== undefined) {
    chunk.toolCalls = toolCalls;
  }
  return { chunk, usage };
}
